import _ from 'lodash';

import {
  TeamMemberFormAccessLevel,
  TeamMemberFormAccessPermissions,
  TeamMemberPermissions,
} from '../../team.constants';
import {
  TeamMemberAccess,
  TeamMemberFormValues,
  TeamMembersData,
  TeamMemberSecurityGroup,
} from '../../team.types';

export const generateSecurityGroups = (
  groups: TeamMemberAccess[],
): string[] => {
  const securityGroups = _.reduce<TeamMemberAccess, string[]>(
    groups,
    (acc, group: TeamMemberAccess) => {
      if (
        group.level === TeamMemberFormAccessLevel.Organization &&
        group.permissions !== TeamMemberFormAccessPermissions.None
      ) {
        acc.push(group.permissions);
      }

      if (
        group.level === TeamMemberFormAccessLevel.Project &&
        group.permissions !== TeamMemberFormAccessPermissions.None
      ) {
        _.forEach(group.projectsIds, (projectId: string) => {
          acc.push(`${group.permissions}@${projectId}`);
        });
      }

      return acc;
    },
    [],
  );

  return _.uniq(securityGroups);
};

export const generateTeamMembersData = (
  formValues: TeamMemberFormValues,
): TeamMembersData => ({
  emails: _.map(formValues.emails, 'id'),
  securityGroups: generateSecurityGroups(formValues.securityGroups),
});

// Team Member Role Helpers:

export const isMemberOrganizationOwner = (
  securityGroups: TeamMemberSecurityGroup[],
): boolean => {
  return _.some(securityGroups, { permission: TeamMemberPermissions.Owner });
};

export const isMemberOrganizationAdmin = (
  securityGroups: TeamMemberSecurityGroup[],
): boolean => {
  return _.some(
    securityGroups,
    ({ permission, projectId }: TeamMemberSecurityGroup) => {
      return permission === TeamMemberPermissions.Admin && !projectId;
    },
  );
};

export const isMemberOrganizationViewer = (
  securityGroups: TeamMemberSecurityGroup[],
): boolean => {
  return _.some(
    securityGroups,
    ({ permission, projectId }: TeamMemberSecurityGroup) => {
      return permission === TeamMemberPermissions.Viewer && !projectId;
    },
  );
};

export const isMemberProjectAdmin = (
  securityGroups: TeamMemberSecurityGroup[],
  selectedProjectId?: string,
): boolean => {
  return _.some(
    securityGroups,
    ({ permission, projectId }: TeamMemberSecurityGroup) => {
      return (
        permission === TeamMemberPermissions.Admin &&
        (selectedProjectId ? projectId === selectedProjectId : projectId)
      );
    },
  );
};

export const isMemberProjectViewer = (
  securityGroups: TeamMemberSecurityGroup[],
  selectedProjectId?: string,
): boolean => {
  return _.some(
    securityGroups,
    ({ permission, projectId }: TeamMemberSecurityGroup) => {
      return (
        permission === TeamMemberPermissions.Viewer &&
        (selectedProjectId ? projectId === selectedProjectId : projectId)
      );
    },
  );
};
